import ScreenBanner from "../../../../../components/ScreenBanner";
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { Checkbox } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { TProduto } from "../../../../../types/api/comercial/produto-estoque";
import PermissionGuard from "../../../../../components/PermissionGuard";
import { useQuery } from "react-query";
import axiosClient from "../../../../../lib/axios";
import DisplayNotFoundInfo from "../../../../../components/DisplayNotFindInfo";
import LoadingApiQuery from "../../../../../components/LoadingApiQuery";
import DialogJuntarProdutos from "./DialogJuntarProdutos";
import SearchWithFilters from "../../../../../components/BasicComponents/SearchWithFilters";
import DialogFiltrosListagem from "../DialogFiltrosListagem";
import ChipList from "../../../../../components/BasicComponents/ChipList";
import { IDataDepartamentosEstoque } from "../../../../../types/api/estoque/pesquisar-departamentos-estoque";
import { IDataSetores } from "../../../../../types/api/estoque/pesquisar-setores";
import { IDataCategorias } from "../../../../../types/api/estoque/pesquisar-categorias";
import useDebounce from "../../../../../lib/useDebounce";
import { getChipFilterItems } from "../../../../../helpers/getChipFilterItems";

export type FilterState = {
  pesquisar?: string;
  departamento?: IDataDepartamentosEstoque[];
  setor?: IDataSetores[];
  categoria?: IDataCategorias[];
};

export default function ListarTodos() {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [filtros, setFiltros] = useState<FilterState>({});
  const [openFilterModal, setOpenFilterModal] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [palavraChave, setPalavraChave] = useState<string>("");
  const [selectedProducts, setSelectedProducts] = useState<TProduto[]>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const deboucedSearchTerm = useDebounce(palavraChave);
  const filters = useMemo(() => {
    return {
      departamento_contem: filtros.departamento ? filtros.departamento.map((dpto) => dpto.id) : null,
      setor_contem: filtros.setor ? filtros.setor.map((setor) => setor.id) : null,
      categoria_contem: filtros.categoria ? filtros.categoria.map((cat) => cat.id) : null,
      pesquisar: deboucedSearchTerm,
    }
  }, [filtros, deboucedSearchTerm])

  const { data, isLoading } = useQuery(
    ["estoque-produtos", page, rowsPerPage, filters],
    async () => {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...filters
      };
      const res = await axiosClient.get("comercial/produtos/pesquisar-produtos", { params });
      return res.data;
    },
    { keepPreviousData: true }
  );

  const dataProdutos: TProduto[] = useMemo(() => data?.data || [], [data]);
  const totalProdutos = data?.total || 0;

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const chipItems = useMemo(() => {
    return getChipFilterItems(
      filtros,
      filtersChipLabels,
    );
  }, [filtros]);

  const handleDeleteFilter = (item: string) => {
    const newFiltros = { ...filtros };

    if (item.startsWith('Departamentos:')) {
      delete newFiltros.departamento;
    } else if (item.startsWith('Setores:')) {
      delete newFiltros.setor;
    } else if (item.startsWith('Categorias:')) {
      delete newFiltros.categoria;
    } else {
      Object.entries(filtros).forEach(([key, value]) => {
        const label = filtersChipLabels[key as keyof typeof filtersChipLabels] || key;
        const displayValue = Array.isArray(value) ? value.join(', ') : value;
        const chipText = `${label}: ${displayValue}`;

        if (chipText === item) {
          delete newFiltros[key as keyof FilterState];
        }
      });
    }

    setFiltros(newFiltros);
  }
  const handleSearch = useCallback(() => {
    setPage(0);
  }, []);
  const handleJuntarProdutos = () => {
    if (selectedProducts.length > 0) {
      setOpenModal(true);
    }
  };

  const handleClose = () => {
    setOpenModal(false);
    setSelectedProducts([]);
  };

  return (
    <>
      <ScreenBanner title="Comercial" />
      <PermissionGuard permissionId="comercial.produtos">
        <Container sx={{ mt: 3 }}>
          <Typography textAlign="center" variant="h5">
            Produtos
          </Typography>

          <Grid mt={1}>
            <Grid size={{ xs: 12 }} ref={containerRef}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ width: "100%", overflowX: "auto" }}>
                  <Box
                    component="form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSearch();
                    }}
                  >
                    <SearchWithFilters
                      palavraChave={palavraChave}
                      textFieldProps={{
                        inputRef: inputRef,
                        label: "Pesquisar",
                        placeholder: "Pesquise por nome ou código do produto",
                        size: "medium",
                      }}
                      pesquisar={handleSearch}
                      setTermoPesquisa={setPalavraChave}
                      handleOpenFilters={() => setOpenFilterModal(true)}
                    />
                  </Box>

                  <ChipList
                    items={chipItems}
                    onDelete={handleDeleteFilter}
                  />

                  <Box sx={{ mt: 2 }}>
                    {selectedProducts.length > 0 && (
                      <Button
                        variant="outlined"
                        size="medium"
                        sx={{ mb: 2 }}
                        startIcon={<FilterListIcon />}
                        onClick={handleJuntarProdutos}
                      >
                        Juntar ({selectedProducts.length}) produtos selecionados
                      </Button>
                    )}
                  </Box>

                  {isLoading ? (
                    <LoadingApiQuery extraParams={{ height: 200, alignItems: 'center' }} />
                  ) : dataProdutos.length === 0 ? (
                    <DisplayNotFoundInfo label="Nenhum produto encontrado" />
                  ) : (
                    <>
                      <TableContainer component={Paper} variant="outlined">
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell width="5%"></TableCell>
                              <TableCell width="15%">Código/Nome do Produto</TableCell>
                              <TableCell width="20%">Departamento</TableCell>
                              <TableCell width="20%">Setor</TableCell>
                              <TableCell width="10%">Unidade</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dataProdutos.map((produto) => (
                              <TableRow
                                key={produto.id}
                                hover
                                sx={{
                                  '&:hover': {
                                    cursor: 'pointer',
                                    backgroundColor: 'action.hover'
                                  },
                                }}
                              >
                                <TableCell>
                                  <Checkbox
                                    checked={selectedProducts.some(p => p.id === produto.id)}
                                    onChange={() => {
                                      setSelectedProducts(prev =>
                                        prev.some(p => p.id === produto.id)
                                          ? prev.filter(p => p.id !== produto.id)
                                          : [...prev, produto]
                                      );
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </TableCell>
                                <TableCell>{produto.produto_estoque?.[0].id} - {produto.nome}</TableCell>
                                <TableCell>
                                  {produto.produto_estoque?.map((item) =>
                                    item.departamento?.nome
                                  ).filter(Boolean).join(', ') || '-'}
                                </TableCell>
                                <TableCell>
                                  {produto.produto_estoque?.map((item) =>
                                    item.setor?.nome
                                  ).filter(Boolean).join(', ') || '-'}
                                </TableCell>
                                <TableCell>
                                  {produto.produto_estoque?.map((item) =>
                                    item.unidade_medida || 'UN'
                                  ).filter(Boolean).join(', ') || 'UN'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                        <TablePagination
                          rowsPerPageOptions={[10, 25, 50]}
                          component="div"
                          count={totalProdutos}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                          labelRowsPerPage="Linhas por página"
                          labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} de ${count !== -1 ? count : `mais que ${to}`}`
                          }
                        />
                      </Box>
                    </>
                  )}
                </Box>

                <DialogFiltrosListagem
                  open={openFilterModal}
                  onClose={() => setOpenFilterModal(false)}
                  onConfirm={(filtros) => {
                    setOpenFilterModal(false)
                    setPage(0)
                    setFiltros(filtros)
                  }}
                  initialFilters={filtros}
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>

        <DialogJuntarProdutos
          open={openModal}
          handleClose={handleClose}
          produtos={selectedProducts}
        />
      </PermissionGuard>
    </>
  );
}

const filtersChipLabels: { [key in keyof FilterState]: string } = {
  categoria: "Categorias",
  departamento: "Departamentos",
  setor: "Setores",
};